'use strict';

import extend from 'lodash/extend';

/**
 * Domain Object Factory, capable of creating domain object
 * trees from given data shuttles and / or type definition.
 */

(function() {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.domain = window.atex.aceui.domain || {};
  window.atex.aceui.domain.impl =  window.atex.aceui.domain.impl || {};

  var DEFAULT_DOMAIN_OBJECT_ID = 'DefaultDomainObject',
      CONTENT_DOMAIN_OBJECT = 'aceUIContentDomainObject',
      ASPECTS_DOMAIN_OBJECT = 'aceUIAspectsDomainObject',
      LIFE_CYCLE_METHODS = ['pack', 'init', 'destroy'];

  function CachingTypeService(Q, TypeService) {
    var cache = {};
    return {
      getTypeDefinition: function (typeName) {

        if (!typeName) {
          return Q.when();
        }

        if (!cache[typeName]) {
          cache[typeName] = TypeService.getTypeDefinition(typeName)
            .then (function (typeData) {
              var response = typeData.typeDefinition.main;

              if (response && response.metaType && response.metaType === 'LIST') {
                response.isList = true;
              }
              return response;
            })
            .catch(function (error) {
              delete cache[typeName];
              throw error;
            });
        }
        return cache[typeName];
      }
    }
  }

  function endsWith (str, suffix) {
    return str.indexOf(suffix, str.length - suffix.length) !== -1;
  }

  function isListTypingProperty (data, property) {
    if (typeof property === 'string' && endsWith(property, '_element_types')) {
      var dataPropertyKey = property.slice(0, property.length - '_element_types'.length);
      return typeof dataPropertyKey !== 'undefined' && typeof data[dataPropertyKey] !== 'undefined';
    }
    return false;
  }

  function createRequirementObject (rootDomainObject, domainObject, path, requirements) {
    var children = domainObject.getChildren ();

    // Make children resolve their dependencies first...
    Object.keys (children).forEach (function (key) {
      createRequirementObject (rootDomainObject, domainObject.getChild(key), path.concat([key]), requirements);
    });

    // ...and then resolve them for the current domain object
    var dod = domainObject.getDefinition ();
    if (dod.requirements) {
      Object.keys(dod.requirements).forEach (function (key) {
        var resolvedDep = atex.aceui.DomainObjectUtils.getDomainObjectByPath (rootDomainObject, dod.requirements[key]);
        atex.aceui.ObjectUtils.accessor (requirements, path.concat(['_requirements', key])).value = resolvedDep;
      });
    }
  }

  atex.aceui.domain.DomainObjectFactory = function (Q, TypeService, DomainObjectService) {

    var cachingTypeService = new CachingTypeService(Q, TypeService);

    var initializeDomainObjectTree = function (domainObjectTree) {
      var requirements = {};

      createRequirementObject(domainObjectTree, domainObjectTree, [], requirements);

      domainObjectTree.init(requirements);
    };

    var loadAspectDomainObject = function (aspectsDomainObjectDefintionInfo, aspectName, aspectData, aspectType) {
      return loadDomainObjectFrom (aspectData, aspectType)
        .then (function (aspectDOInfo) {
          aspectsDomainObjectDefintionInfo.addChild (aspectName, aspectDOInfo);
        });
    };

    var getDomainObjectDefinitionInfoFor = function (domainObjectDefinitionId, typeName, arrayTypeName) {
      return DomainObjectService.getDomainObjectDefinition(domainObjectDefinitionId, typeName, arrayTypeName).then(function(domainObjectDefinition) {
        return Q.when(atex.aceui.domain.DomainObjectDefinitionInfo (domainObjectDefinition));
      });
    };

    var instantiateDefinitionInfo = function(data, dataPath, definitionInfo) {
      var currentData = atex.aceui.ObjectUtils.getByPath(data, dataPath);

      var accessor = atex.aceui.ObjectUtils.accessor(data, dataPath);
      var domainObject = Object.create(atex.aceui.domain.DomainObject (accessor, definitionInfo));

      domainObject.path = (typeof dataPath !== 'undefined' && dataPath.length > 0) ? '/' + dataPath.join('/') : '/';

      var customDefinition = definitionInfo.getDefinition().customDefinition || {};

      LIFE_CYCLE_METHODS.forEach(function(key) {
        if (typeof customDefinition[key] !== 'undefined') {
          customDefinition[key + 'Self'] = customDefinition[key];
          delete customDefinition[key];
        }
      });

      extend(domainObject, customDefinition);

      if (typeof currentData === 'undefined') {
        if (typeof definitionInfo.getDefinition().createEmptyData !== 'undefined') {
          currentData = definitionInfo.getDefinition().createEmptyData();
          accessor.value = currentData;
        } else if (Object.keys(definitionInfo.getChildren()).length > 0) {
          currentData = {};
          accessor.value = currentData;
        }
      }

      if (typeof currentData !== 'undefined') {
        Object.keys(definitionInfo.getChildren()).forEach(function(childName) {
          var newDataPath = dataPath.concat([childName]);
          var childDefinitionInfo = definitionInfo.getChild(childName);

          var childDomainObject = instantiateDefinitionInfo(data, newDataPath, childDefinitionInfo);

          childDomainObject.parent = domainObject;
          domainObject.addChild(childName, childDomainObject);
        });
      }

      return domainObject;
    };

    var loadDomainObjectFrom = function(data, typeId) {
      data = data || {};

      var promises = {};

      return cachingTypeService.getTypeDefinition(typeId).then (function(type) {
        var domainObjectId = type && type.name || DEFAULT_DOMAIN_OBJECT_ID;
        var elementType = undefined;
        var isList = type && type.metaType && type.metaType === 'LIST';

        if (isList) {
          domainObjectId = type.metaType;
          elementType = type.elementType;
        }

        return getDomainObjectDefinitionInfoFor(domainObjectId, typeId, elementType).then(function (definitionInfo) {
          var properties = [];

          if (data === Object(data)) {
            properties = Object.keys(data);
          }

          for (var i = 0; i < properties.length; i++) {
            var property = properties[i];

            if (property !== '_type' && !isList && !isListTypingProperty(data, property)) {

              (function (property) {
                var type = undefined;
                var subData = undefined;

                var promiseKey = undefined;

                if (property.indexOf('_type') !== -1) {
                  promiseKey = property.replace('_type', '');
                  type = data[property];

                  if (typeof data[promiseKey] !== 'undefined') {
                    type = type || data[promiseKey]._type;
                  }

                  subData = atex.aceui.ObjectUtils.getByPath(data, [promiseKey]) || {};
                } else {
                  type = data[property]._type || data[property + '_type'];
                  subData = data[property];

                  promiseKey = property;
                }

                if (typeof type !== 'undefined' && typeof promises[promiseKey] === 'undefined') {
                  promises[promiseKey] = loadDomainObjectFrom(subData, type).then(function (childDefinitionInfo) {
                    definitionInfo.addChild(promiseKey, childDefinitionInfo);
                  });
                }
              })(property);

            }
          }

          // Type definition...
          if (typeof type !== 'undefined') {
            Object.keys (type.fields).forEach(function (fieldName) {
              if (!promises[fieldName]) {

                var authoritativeType = data[fieldName + '_type'] || type.fields[fieldName];
                var attributeData = atex.aceui.ObjectUtils.getByPath (data, [fieldName]) || {};

                promises[fieldName] = loadDomainObjectFrom (attributeData, authoritativeType)
                  .then (function (childDefinitionInfo) {
                    definitionInfo.addChild (fieldName, childDefinitionInfo);
                  });
              }
            });
          }

          return Q.all(Object.values(promises)).then(function() {

            /**
             * DomainObjectDefinition.customizeDefinition is a customizable entry point
             * for unknown project / customer code, so we make sure to properly
             * catch any errors thrown.
             */

            try {
              definitionInfo.getDefinition().customizeDefinition(definitionInfo);
            } catch (error) {
              throw atex.aceui.error.Error({
                origin: 'atex.aceui.domain.DomainObjectFactory',
                severity: atex.aceui.error.severities.MAJOR,
                type: atex.aceui.error.types.UNKNOWN,

                rootCause: error,

                cause: "An error was thrown during execution of DomainObjectDefinition.customizeDefinition" +
                       " for Domain Object Definition '" + definitionInfo.getDefinition().getId() + "'"
              });
            }

            return definitionInfo;
          });
        });
      });
    };

    function resolveAspectsUsedByTemplate (template) {
      let aspects = [];

      if (template && template.namePath) {
        aspects.push(template.namePath.split('/')[0])
      }

      if (template && template.dataBindings) {
        template.dataBindings.forEach ((binding) => {
          if (binding.domainObject) {
            aspects.push (binding.domainObject.split('/')[0] || '/');
          } else if (binding.domainObjects) {
            Object.values (binding.domainObjects).forEach
              ((value) => aspects.push (value.split('/')[0] || '/'));
          }
        });
      }

      return aspects.filter((v, i, a) => a.indexOf(v) === i);
    }

    var loadAndInstantiateDomainObjectFrom = function(data, template) {
      template = template || {};

      if (typeof data.aspects === 'undefined') {
        var error = atex.aceui.error.Error({
          origin: 'atex.aceui.domain.DomainObjectFactory',
          severity: atex.aceui.error.severities.MAJOR,
          type: atex.aceui.error.types.INVALID_PARAMETERS,

          cause: "Cannot load Domain Object from empty data (missing 'aspects' property)!"
        });

        return Q.reject(error);
      }

      var aspectsData = data.aspects;

      var staticDOPromises = [ getDomainObjectDefinitionInfoFor (CONTENT_DOMAIN_OBJECT),
                               getDomainObjectDefinitionInfoFor (ASPECTS_DOMAIN_OBJECT) ];

      return Q.all(staticDOPromises).then(function(results) {
        var contentDomainObjectDefinitionInfo = results[0];
        var aspectsDomainObjectDefinitionInfo = results[1];

        contentDomainObjectDefinitionInfo.addChild('aspects', aspectsDomainObjectDefinitionInfo);

        var aspectDomainDefinitionInfoPromises = Object.keys(aspectsData).map(function(aspectName) {
          var aspectData = aspectsData[aspectName];
          var aspectType = (aspectData || {})._type;

          return loadAspectDomainObject(aspectsDomainObjectDefinitionInfo, aspectName, aspectData, aspectType);
        });

        return Q.all(aspectDomainDefinitionInfoPromises).then(function() {
          var requestedAspects = resolveAspectsUsedByTemplate (template);

          if (typeof requestedAspects !== 'undefined') {
            var aspectPromises = requestedAspects.map(function (aspectName) {
              var existingAspectDomainObject = aspectsDomainObjectDefinitionInfo.getChild(aspectName);

              if (typeof existingAspectDomainObject !== 'undefined') {
                if (aspectName !== existingAspectDomainObject.getDefinition().getTypeId()) {
                  var error = atex.aceui.error.Error({
                    origin: 'atex.aceui.domain.DomainObjectFactory',
                    severity: atex.aceui.error.severities.MAJOR,
                    type: atex.aceui.error.types.CONFLICT,

                    cause: "Type of requested template aspect '" + aspectName + "'" +
                           " conflicts with existing type '" + existingAspectDomainObject.getDefinition().getTypeId() + "'!"
                  });

                  return Q.reject(error);
                }

                return Q.when();
              }

              return loadAspectDomainObject(aspectsDomainObjectDefinitionInfo, aspectName, { }, aspectName);
            });

            return Q.all(aspectPromises).then(function() {
              return contentDomainObjectDefinitionInfo;
            });
          }

          return contentDomainObjectDefinitionInfo;
        });
      }).then(function(domainObjectDefinitionInfo) {
        return instantiateDefinitionInfo(data, [], domainObjectDefinitionInfo);
      }).then(function(domainObjectTree) {
        initializeDomainObjectTree(domainObjectTree);

        return domainObjectTree;
      });
    };

    return {
      _getTypeDefinitionFor: function(typeName) {
        return cachingTypeService.getTypeDefinition(typeName);
      },
      _getDomainObjectDefinitionInfoFor: getDomainObjectDefinitionInfoFor,
      _instantiateDefinitionInfo: instantiateDefinitionInfo,
      _loadDomainObjectFrom: loadDomainObjectFrom,
      _loadAndInstantiateDomainObjectFrom: loadAndInstantiateDomainObjectFrom,

      fromData: function (data, template) {
        if (!data) {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.domain.DomainObjectFactory',
            severity: atex.aceui.error.severities.MAJOR,
            type: atex.aceui.error.types.INVALID_PARAMETERS,

            cause: "Cannot load Domain Object from undefined data!"
          });

          return Q.reject(error);
        }

        var dataCopy = JSON.parse(JSON.stringify(data));

        return this._loadAndInstantiateDomainObjectFrom(dataCopy, template).then(function (domainObject) {
          domainObject.isRoot = true;
          return domainObject;
        });
      },

      fromType: function (typeId, template) {
        var error = atex.aceui.error.Error({
          origin: 'atex.aceui.domain.DomainObjectFactory',
          severity: atex.aceui.error.severities.MAJOR,
          type: atex.aceui.error.types.NOT_IMPLEMENTED,

          cause: "Cannot instantiate Domain Object from type!"
        });

        return Q.reject(error);
      }
    }
  };
})();
